<template>
    <div v-if="showQuestionIndex == null">
        <div class="flex flex-col gap-4 bg-gray-200 p-8 p-8 text-center">
            <div class="text-[20px] font-semibold" ref="FrontendPracticeBackToResults">Great Job!</div>
            <div class="">
                You scored
                <span class="handwritten-underline-auto font-semibold"
                    >{{ score }}%</span
                >. You can click on “Review Answers” to see the correct answers
                for each question.
            </div>
            <div class="">
                <span class="btn btn-primary" @click="reviewAnswers"
                    >Review Answers</span
                >
            </div>
        </div>
        <div class="">
            <div class="mb-4 mt-8 font-semibold">Your Results</div>
            <div class="" ref="FrontendPracticeQuizQuestion">
                <div
                    class="flex items-center gap-8 border-t border-gray-500 py-4"
                    v-for="(question, index) in gradedQuizQuestions"
                >
                    <div class="w-4 flex-none" v-if="question.was_correct">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="10" cy="10" r="10" fill="#22B141" />
                            <path
                                d="M5.55566 10.4448C7.27106 11.63 9.76619 13.3781 9.76619 14.4448C10.5459 11.7781 12.5732 6.26702 14.4446 5.55591"
                                stroke="white"
                                stroke-width="1.66667"
                            />
                        </svg>
                    </div>
                    <div class="w-4 flex-none" v-else>
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="10" cy="10" r="10" fill="#FA4E37" />
                            <path
                                d="M6.36377 5.90894L13.6365 14.0908"
                                stroke="white"
                                stroke-width="1.13636"
                                stroke-linecap="round"
                            />
                            <path
                                d="M13.6362 5.90869L6.3635 14.0905"
                                stroke="white"
                                stroke-width="1.13636"
                                stroke-linecap="round"
                            />
                        </svg>
                    </div>
                    <div class="">
                        <text-clamp
                            autoResize
                            :max-lines="2"
                            :text="question.stripped_content"
                        >
                            <template #before
                                ><span class="mr-1 font-semibold"
                                    >{{ index + 1 }}.</span
                                ></template
                            >
                        </text-clamp>
                    </div>
                    <div class="text-right">
                        <span
                            class="link whitespace-nowrap"
                            @click="seeExplanation(index)"
                            >See Explanation</span
                        >
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div v-else class="">
        <div class="mb-4 flex items-center justify-between text-gray-600">
            <div class="">
                {{ showQuestionIndex + 1 }} of {{ gradedQuizQuestions.length }}
            </div>
            <div class="link !text-gray-600" @click="backToResults">
                Back to results
            </div>
        </div>
        <div class="mb-4">
            <span
                v-html="gradedQuizQuestions[showQuestionIndex].content"
            ></span>
        </div>
        <div class="flex flex-col gap-2">
            <div
                v-for="answer in gradedQuizQuestions[showQuestionIndex].answers"
            >
                <div
                    :class="
                        'mb-4 flex items-center justify-between gap-8 rounded border border-gray-400 p-4 outline outline-4 outline-transparent' +
                        ' ' +
                        getAnswerStyle(answer)
                    "
                >
                    <div class="flex flex-col">
                        <div
                            class=""
                            v-if="answer.isSelected && !answer.isCorrect"
                        >
                            Your answer:
                        </div>
                        <div class="" v-if="answer.isCorrect">
                            Correct answer:
                        </div>
                        <div class="text-gray-800">{{ answer.text }}</div>
                    </div>
                    <div class="w-4 flex-none" v-if="answer.isCorrect">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="10" cy="10" r="10" fill="#22B141" />
                            <path
                                d="M5.55566 10.4448C7.27106 11.63 9.76619 13.3781 9.76619 14.4448C10.5459 11.7781 12.5732 6.26702 14.4446 5.55591"
                                stroke="white"
                                stroke-width="1.66667"
                            />
                        </svg>
                    </div>
                    <div class="w-4 flex-none" v-else-if="answer.isSelected">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="10" cy="10" r="10" fill="#FA4E37" />
                            <path
                                d="M6.36377 5.90894L13.6365 14.0908"
                                stroke="white"
                                stroke-width="1.13636"
                                stroke-linecap="round"
                            />
                            <path
                                d="M13.6362 5.90869L6.3635 14.0905"
                                stroke="white"
                                stroke-width="1.13636"
                                stroke-linecap="round"
                            />
                        </svg>
                    </div>
                </div>
            </div>
        </div>
        <div
            class="my-8 rounded bg-gray-100 p-4"
            v-if="gradedQuizQuestions[showQuestionIndex].answer_rationale"
        >
            <div class="mb-4 font-semibold">Explanation</div>
            {{ gradedQuizQuestions[showQuestionIndex].answer_rationale }}
        </div>
        <div class="mt-4 flex items-center justify-between">
            <span
                @click="prevQuestion"
                :class="
                    'btn btn-lg btn-primary' +
                    ' ' +
                    (showQuestionIndex > 0 ? '' : 'btn-disabled')
                "
                >Prev Question</span
            >
            <span
                v-if="showQuestionIndex + 1 < gradedQuizQuestions.length"
                @click="nextQuestion"
                :class="'btn btn-lg btn-primary'"
                >Next Question
            </span>
            <span
                v-else
                class="btn btn-secondary btn-lg"
                @click="backToResults"
                >Back to results</span
            >
        </div>
    </div>
</template>
<script>
export default {
    name: "FrontendPracticeQuizResults",
    props: {
        gradedQuizQuestions: {},
        score: {},
    },
    data() {
        return {
            showQuestionIndex: null,
        };
    },
    methods: {
        prevQuestion() {
            this.showQuestionIndex > 0
                ? this.showQuestionIndex--
                : (this.showQuestionIndex = 0);
            
            this.$emit("practice-question-answered");
        },
        nextQuestion() {
            this.showQuestionIndex = this.showQuestionIndex + 1;
            this.$emit("practice-question-answered");
        },
        reviewAnswers () {
            this.showQuestionIndex = 0;
            this.$emit("practice-question-answered");
        },
        backToResults () {
            this.$emit("practice-question-answered", () => {
                this.showQuestionIndex = null;
            });
        },
        seeExplanation (index) {
            this.showQuestionIndex = index
            this.$emit("practice-question-answered");
        },
        getAnswerStyle(answer) {
            let styles = "";

            if (answer.isSelected && !answer.isCorrect) {
                styles = "border-red-300 text-red-300";
            }

            if (answer.isCorrect) {
                styles = "border-green-300 text-green-300";
            }

            return styles;
        },
    },
};
</script>
